import { useState, useMemo } from 'react';
import { Plus, DollarSign, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { PodusCheckerNavigation } from '@/components/Navbar';
import CreateWithdrawalModal from '@/components/modals/CreateWithdrawalModal';
import AdminWithdrawals from './AdminWithdrawals';
import { 
  useGetMyWithdrawalsQuery, 
  useGetMyEarningsSummaryQuery,
  useGetMyBankAccountsQuery 
} from '@/store/api/apiSlice';
import { Withdrawal, WithdrawalStatus, UserRole } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

type FilterType = 'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed';

export default function Withdrawals() {
  const currentUser = useAppSelector(selectCurrentUser);

  const [filter, setFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch data
  const { data: withdrawals = [], isLoading } = useGetMyWithdrawalsQuery();
  const { data: summary } = useGetMyEarningsSummaryQuery();
  const { data: bankAccounts = [] } = useGetMyBankAccountsQuery();

  // Calculate available balance
  const availableBalance = useMemo(() => {
    const totalPaid = summary?.paidEarnings || 0;
    const completedWithdrawals = withdrawals
      .filter(w => w.status === WithdrawalStatus.COMPLETED)
      .reduce((sum, w) => sum + w.amount, 0);
    const pendingWithdrawals = withdrawals
      .filter(w => [WithdrawalStatus.PENDING, WithdrawalStatus.APPROVED, WithdrawalStatus.PROCESSING].includes(w.status))
      .reduce((sum, w) => sum + w.amount, 0);

    return totalPaid - completedWithdrawals - pendingWithdrawals;
  }, [summary, withdrawals]);

  // Filter withdrawals
  const filteredData = useMemo(() => {
    return withdrawals.filter((w) => {
      if (filter === 'all') return true;
      return w.status.toLowerCase() === filter;
    });
  }, [withdrawals, filter]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  // Render admin view for SUPER_ADMIN
  if (currentUser?.role === UserRole.SUPER_ADMIN) {
    return <AdminWithdrawals />;
  }

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleRowSelect = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: WithdrawalStatus) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case WithdrawalStatus.APPROVED:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case WithdrawalStatus.PROCESSING:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case WithdrawalStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-100';
      case WithdrawalStatus.REJECTED:
        return 'bg-red-50 text-red-700 border-red-100';
      case WithdrawalStatus.FAILED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const columns: Column<Withdrawal>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (_item, index) => (
        <span className="text-sm text-gray-700">
          {(currentPage - 1) * rowsPerPage + (index || 0) + 1}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm font-semibold text-gray-900">
          {formatCurrency(item.amount)}
        </span>
      ),
    },
    {
      key: 'bankAccount',
      label: 'Bank Account',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">{item.bankAccount?.bankName}</p>
          <p className="text-xs text-gray-500">{item.bankAccount?.accountNumber}</p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${getStatusColor(item.status)} px-3 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date Requested',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">
          {new Date(item.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'processedAt',
      label: 'Date Processed',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">
          {item.processedAt
            ? new Date(item.processedAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '-'}
        </span>
      ),
    },
  ];

  const hasPendingWithdrawal = withdrawals.some(w => w.status === WithdrawalStatus.PENDING);

  if (isLoading) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading withdrawals...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <PodusCheckerNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
          userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-8 lg:p-10">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Withdrawals</h1>
              {bankAccounts.length > 0 && (
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  disabled={hasPendingWithdrawal}
                  className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Request Withdrawal
                </Button>
              )}
            </div>

            {hasPendingWithdrawal && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  You have a pending withdrawal request. Please wait for it to be processed before creating a new one.
                </p>
              </div>
            )}

            {bankAccounts.length === 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <p className="text-orange-800 text-sm">
                  Please add a bank account in Settings before requesting a withdrawal.
                </p>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-green-50 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-sm text-green-600 font-medium">Available Balance</p>
                </div>
                <p className="text-3xl font-bold text-green-900">{formatCurrency(availableBalance)}</p>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-sm text-blue-600 font-medium">Total Earned</p>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-blue-900">{formatCurrency(summary?.totalEarnings || 0)}</p>
              </div>

              <div className="bg-yellow-50 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <p className="text-sm text-yellow-600 font-medium">Pending</p>
                </div>
                <p className="text-3xl font-bold text-yellow-900">
                  {formatCurrency(
                    withdrawals
                      .filter(w => [WithdrawalStatus.PENDING, WithdrawalStatus.APPROVED, WithdrawalStatus.PROCESSING].includes(w.status))
                      .reduce((sum, w) => sum + w.amount, 0)
                  )}
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Withdrawn</p>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(
                    withdrawals
                      .filter(w => w.status === WithdrawalStatus.COMPLETED)
                      .reduce((sum, w) => sum + w.amount, 0)
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 overflow-x-auto">
            <button
              onClick={() => setFilter('all')}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Completed
            </button>
          </div>

          {/* Table */}
          <div className="border border-gray-100 rounded-2xl overflow-hidden mb-6">
            {paginatedData.length > 0 ? (
              <DataTable
                data={paginatedData}
                columns={columns}
                showCheckbox={true}
                selectedRows={selectedRows}
                onRowSelect={handleRowSelect}
                hoverable={true}
                striped={true}
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">No withdrawal requests found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {paginatedData.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>

                {currentPage > 2 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      1
                    </button>
                    {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
                  </>
                )}

                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    {currentPage - 1}
                  </button>
                )}

                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
                  {currentPage}
                </button>

                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    {currentPage + 1}
                  </button>
                )}

                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Withdrawal Modal */}
      <CreateWithdrawalModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        availableBalance={availableBalance}
      />
    </main>
  );
}
